import { enqueueSnackbar } from 'notistack';
import { Box, Button } from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

import ApiClient from '@/src/utils/ApiClient';
import UseNavigation from '@/src/hooks/useNavigation';
import { IApiClientResponse, ILooseObject } from '@/src/types/common.interface';
import { store } from '@/src/datastore/store';
import AllPolicy, { ALL_POLICY_URLS_ENUM } from '@/src/config/api_config/allPolicies';
import AppConfig from '@/src/config/AppConfig';
import TableHeadersConfig from '@/src/config/TableHeadersConfig';

type CreateNewPolicyParams = {
  applicationKey: string;
  payload: ILooseObject;
};

export interface IGetPolicyListParams {
  applicationKey: string;
}

export interface IExecutePolicyParams {
  body: ILooseObject;
}

export interface IGetPolicyListResult {
  rows: ILooseObject[];
  columns: GridColDef[];
  error: IApiClientResponse['error'];
}

type CreateNewPolicyResult = {
  data: ILooseObject;
  error: {
    code: number;
    message: string;
  };
};

const updateTableColumns = (headers: GridColDef[]): GridColDef[] => {
  return headers.map((header: GridColDef) => {
    if (typeof header === 'object' && header !== null) {
      const tableColumns: GridColDef = {
        ...header
      };
      return tableColumns;
    } else {
      return {} as GridColDef;
    }
  });
};

const updateDefaultColumns = (applicationKey: string): GridColDef[] => {
  const defaultColumns: GridColDef[] = [];
  defaultColumns.push({
    field: 'Actions',
    headerName: 'Actions',
    sortable: false,
    filterable: false,
    flex: 1,
    minWidth: 150,
    renderCell: (params: GridRenderCellParams) => {
      const navigation = UseNavigation();
      return (
        <Box component="div" sx={{ my: 1 }}>
          <Button
            variant="text"
            size="small"
            endIcon={<NavigateNextIcon />}
            color={'primary'}
            disabled
            sx={{ textTransform: 'none' }}
            onClick={() => {
              navigation.navigateTo(`/policy/policy-details`);
            }}
          >
            View details
          </Button>
        </Box>
      );
    }
  });
  return defaultColumns;
};

abstract class AllPolicyService {
  public static async getAllPolicyList(params: IGetPolicyListParams) {
    const { applicationKey } = params;
    const result: IGetPolicyListResult = {
      rows: [],
      columns: [],
      error: {
        code: 0,
        message: ''
      }
    };
    try {
      const response = await ApiClient.makeRequest({
        url: AllPolicy.GetPolicyApiUrl({
          url: ALL_POLICY_URLS_ENUM.GET_ALL_POLICY_LIST,
          data: { applicationKey }
        }),
        method: 'GET'
      });

      if (response.data) {
        const tableColumns: GridColDef[] = updateTableColumns(
          TableHeadersConfig.ALL_POLICY_LIST || []
        );
        const userPermissions = store.getState()?.userDetails?.permissions ?? [];
        const applicationKey = response.data?.data?.application_key;
        const defaultColumns: GridColDef[] =
          userPermissions?.includes('all_user_view') || userPermissions?.includes('policy_view')
            ? updateDefaultColumns(applicationKey)
            : [];
        result.columns = [...tableColumns, ...defaultColumns];
        result.rows = response.data?.data ?? [];
      } else {
        result.error = {
          ...response.error
        };
      }
    } catch (error) {
      enqueueSnackbar(error?.toString(), {
        variant: 'error',
        autoHideDuration: AppConfig.SNACKBAR_AUTO_HIDE_DURATIONS.error
      });
    }
    return result;
  }

  public static async createNewPolicy(
    params: CreateNewPolicyParams
  ): Promise<CreateNewPolicyResult> {
    const { applicationKey, payload } = params;

    const result: CreateNewPolicyResult = {
      data: {},
      error: {
        code: 0,
        message: ''
      }
    };

    try {
      const response: IApiClientResponse = await ApiClient.makeRequest({
        url: AllPolicy.GetPolicyApiUrl({
          url: ALL_POLICY_URLS_ENUM.CREATE_NEW_POLICY,
          data: { applicationKey }
        }),
        method: 'POST',
        body: payload
      });

      if (response.data) {
        result.data = response.data;
      } else {
        result.error = { ...response.error };
      }
    } catch (error) {
      enqueueSnackbar(error?.toString(), {
        variant: 'error',
        autoHideDuration: AppConfig.SNACKBAR_AUTO_HIDE_DURATIONS.error
      });
    }

    return result;
  }

  public static async executePolicy(params: IExecutePolicyParams): Promise<CreateNewPolicyResult> {
    const { body } = params;

    const result: CreateNewPolicyResult = {
      data: {},
      error: {
        code: 0,
        message: ''
      }
    };

    try {
      const response: IApiClientResponse = await ApiClient.makeRequest({
        url: AllPolicy.GetPolicyApiUrl({
          url: ALL_POLICY_URLS_ENUM.EXECUTE_POLICY
        }),
        method: 'POST',
        body
      });

      if (response.data) {
        result.data = response.data;
      } else {
        result.error = { ...response.error };
      }
    } catch (error) {
      enqueueSnackbar(error?.toString(), {
        variant: 'error',
        autoHideDuration: AppConfig.SNACKBAR_AUTO_HIDE_DURATIONS.error
      });
    }

    return result;
  }
}

export default AllPolicyService;
